import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Input, Label, Modal, Textarea } from '../../../shared/components';
import { 
  Save,
  Loader2,
  User,
  Phone,
  Calendar,
  Hash,
  BookOpen,
  FileText
} from 'lucide-react';
import { eventService } from '../../../shared/services';
import { filterSelectableEvents } from '../../../shared/utils/eventStatus';

/**
 * Formulario de Alumno optimizado para modal
 * Maneja creación y edición de Alumnos
 * Adaptado a los campos del CreateStudentDto del backend
 */
const StudentFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  student = null, 
  title = "Nuevo Alumno",
  submitText = "Crear Alumno"
}) => {
  // Estados del formulario - adaptado al DTO del backend
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    eventId: '',
    code: '',
    course: '',
    description: '',
    isActive: true,
  });

  // Estados de validación y carga
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Estado para eventos disponibles
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Estado inicial del formulario
  const initialFormData = useMemo(() => ({
    name: '',
    phone: '',
    eventId: '',
    code: '',
    course: '',
    description: '',
    isActive: true,
  }), []);

  // Función para limpiar el formulario
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
  }, [initialFormData]);

  // Cargar eventos disponibles
  const loadEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      const eventsData = await eventService.getEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Error al cargar eventos:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // Cargar eventos al montar el componente
  useEffect(() => {
    if (isOpen) {
      loadEvents();
    }
  }, [isOpen, loadEvents]);

  // Inicializar formulario con datos del Alumno si está en modo edición
  useEffect(() => {
    if (student) {
      // Extraer eventId de los accessCodes del estudiante
      let eventId = '';
      if (student.accessCodes && student.accessCodes.length > 0) {
        eventId = student.accessCodes[0].eventId || '';
      }
      
      setFormData({
        name: student.name || '',
        phone: student.phone || '',
        eventId: eventId,
        code: student.code || '',
        course: student.course || '',
        description: student.description || '',
        isActive: student.isActive !== undefined ? student.isActive : true,
      });
    } else {
      // Resetear formulario para creación
      resetForm();
    }
  }, [student, resetForm]);

  // Limpiar errores al cerrar modal
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
    }
  }, [isOpen]);

  // Formatear para mostrar: title - time (HH:mm)
  const formatEventDisplay = (event) => {
    if (!event) return '';
    const time = (event.eventTime || event.time || '').toString();
    const hhmm = time ? time.slice(0,5) : '';
    return hhmm ? `${event.title} - ${hhmm}` : event.title;
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Validar nombre completo
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre completo es requerido';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres';
    } else if (formData.name.trim().length > 200) {
      newErrors.name = 'El nombre no puede exceder 200 caracteres';
    }

    // Validar función (evento) - solo requerida para creación
    if (!student && !formData.eventId.trim()) {
      newErrors.eventId = 'La función (evento) es requerida';
    }

    // Validar código
    if (!formData.code.trim()) {
      newErrors.code = 'El código del alumno es requerido';
    } else if (formData.code.trim().length < 2) {
      newErrors.code = 'El código debe tener al menos 2 caracteres';
    } else if (formData.code.trim().length > 50) {
      newErrors.code = 'El código no puede exceder 50 caracteres';
    }

    // Validar curso
    if (!formData.course.trim()) {
      newErrors.course = 'El curso es requerido';
    } else if (formData.course.trim().length < 2) {
      newErrors.course = 'El curso debe tener al menos 2 caracteres';
    } else if (formData.course.trim().length > 100) {
      newErrors.course = 'El curso no puede exceder 100 caracteres';
    }

    // Validar teléfono (solo números y +)
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else if (!/^[\d+]+$/.test(formData.phone.trim())) {
      newErrors.phone = 'El teléfono solo puede contener números y el signo +';
    } else if (formData.phone.trim().length > 20) {
      newErrors.phone = 'El teléfono no puede exceder 20 caracteres';
    }

    // Validar descripción (opcional)
    if (formData.description && formData.description.trim().length > 500) {
      newErrors.description = 'La descripción no puede exceder 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Preparar datos para envío
      const submitData = { ...formData };
      
      // Si estamos editando un estudiante, no enviar eventId (no se puede cambiar)
      if (student) {
        delete submitData.eventId;
      }
      
      await onSubmit(submitData);
      resetForm();
    } catch (error) {
      console.error('Error en el formulario:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={title}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Función (Evento) */}
        <div>
          <Label htmlFor="eventId" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            EVENTO *
          </Label>
          <select
            id="eventId"
            name="eventId"
            value={formData.eventId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.eventId ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading || eventsLoading || !!student}
          >
            <option value="">Seleccionar evento</option>
            {filterSelectableEvents(events, formData.eventId).map((event) => (
              <option key={event.id} value={event.id}>
                {formatEventDisplay(event)}
              </option>
            ))}
          </select>
          {errors.eventId && (
            <p className="mt-1 text-sm text-red-600">{errors.eventId}</p>
          )}
          {eventsLoading && (
            <p className="mt-1 text-xs text-gray-500">Cargando eventos...</p>
          )}
          {student && (
            <p className="mt-1 text-xs text-gray-500">El evento no se puede cambiar al editar un alumno</p>
          )}
        </div>

        {/* Código del Alumno */}
        <div>
          <Label htmlFor="code" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Hash className="w-4 h-4" />
            CÓDIGO ALUMNO *
          </Label>
          <Input
            id="code"
            name="code"
            type="text"
            value={formData.code}
            onChange={handleInputChange}
            placeholder="Código único del alumno"
            className={`w-full ${errors.code ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
            maxLength={50}
          />
          {errors.code && (
            <p className="mt-1 text-sm text-red-600">{errors.code}</p>
          )}
        </div>

        {/* Nombre Completo */}
        <div>
          <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <User className="w-4 h-4" />
            NOMBRE COMPLETO *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Ingrese el nombre completo del Alumno (ej: Juan Pérez)"
            className={`w-full ${errors.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
            maxLength={200}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Incluya nombre y apellido en un solo campo
          </p>
        </div>

        {/* Teléfono Celular */}
        <div>
          <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Phone className="w-4 h-4" />
            TELÉFONO CELULAR *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="+5491112345678"
            className={`w-full ${errors.phone ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
            maxLength={20}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Solo números y el signo + (ej: +5491112345678)
          </p>
        </div>

        {/* Curso */}
        <div>
          <Label htmlFor="course" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            CURSO (Código de curso) *
          </Label>
          <Input
            id="course"
            name="course"
            type="text"
            value={formData.course}
            onChange={handleInputChange}
            placeholder="ej: 1A, 2B, Avanzado"
            className={`w-full ${errors.course ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
            maxLength={100}
          />
          {errors.course && (
            <p className="mt-1 text-sm text-red-600">{errors.course}</p>
          )}
        </div>

        {/* Descripción */}
        <div>
          <Label htmlFor="description" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <FileText className="w-4 h-4" />
            DESCRIPCIÓN (Descripción del curso)
          </Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Descripción del curso o información adicional..."
            rows={3}
            className={`w-full ${errors.description ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
            maxLength={500}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Campo opcional para información adicional del curso
          </p>
        </div>

        {/* Estado activo */}
        <div className="flex items-center">
          <input
            id="isActive"
            name="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={loading}
          />
          <Label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
            Alumno activo
          </Label>
        </div>

        {/* Información adicional */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">
            Información
          </h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Los campos marcados con * son obligatorios</li>
            <li>• La descripción es opcional</li>
            <li>• El teléfono solo acepta números y el signo +</li>
            <li>• Cada alumno debe tener un código único</li>
            <li>• Incluya nombre y apellido en el campo "Nombre Completo"</li>
            <li>• Los alumnos inactivos no podrán realizar nuevas reservas</li>
          </ul>
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {submitText}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default StudentFormModal;